/**
 * Which coverage notes a /looker tile carries (spec §9.2, §9.3), and box 8's
 * coverage lines. Shared by the page, the chat and the CSV, so all three say
 * the same thing. Pure: labels come in already translated.
 *
 * - A Result tile by objective: each objective row gets that objective's gaps.
 * - A Result tile with no objective dimension: the card gets the gaps of the
 *   one selected objective, and nothing when no single one is selected.
 * - A table whose first dimension is the campaign name: a non-conforming row
 *   is chipped "Unclassified". This needs no coverage read.
 * - No coverage (idle, loading, failed): no Result notes.
 *
 * @module services/looker/lookerTileNotes
 */
import type { CoverageState } from '@/hooks/useLookerCoverage';
import { classifyCampaignName } from './campaignNaming';
import { fmtNamingCoverage, type CoverageMetric, type LookerCoverage, type MetricGap, type ResultObjective } from './lookerCoverage';
import { rowKey, type LookerField, type LookerRow, type LookerTile } from './lookerTypes';

export interface TileNotes {
  /** Card footer lines, e.g. "google ไม่ส่ง Conversions". */
  card: string[];
  /** rowKey → chips beside the row's first cell. Only rows with a note are present. */
  rows: Record<string, string[]>;
}

export interface TileNoteLabels {
  notReported: (platform: string, metric: CoverageMetric) => string;
  unclassified: string;
}

export const NO_TILE_NOTES: TileNotes = { card: [], rows: {} };

/** The same match lookerChatContext and api/_lib/lookerResult use; a pivoted series `measure|key` counts. */
const RESULT = /(^|\.)(result|cost_per_result)$/;
const isResultField = (f: LookerField): boolean => RESULT.test(f.name.split('|')[0]);
const OBJECTIVES_DIM = /(^|\.)objectives$/;
const CAMPAIGN_DIM = /(^|\.)campaign_name$/;
const RESULT_OBJECTIVES: readonly string[] = ['awareness', 'consideration', 'conversion'];

const asObjective = (v: unknown): ResultObjective | null => {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return RESULT_OBJECTIVES.includes(s) ? (s as ResultObjective) : null;
};

const gapText = (gaps: readonly MetricGap[], labels: TileNoteLabels): string[] =>
  gaps.map((g) => labels.notReported(g.platform, g.metric));

const unique = (xs: readonly string[]): string[] => [...new Set(xs)];

/** rowKey → Result-gap notes for a tile with an objectives dimension. */
const objectiveRowNotes = (tile: LookerTile, dim: LookerField, coverage: LookerCoverage, labels: TileNoteLabels): Array<[string, string[]]> =>
  tile.rows.flatMap((r): Array<[string, string[]]> => {
    const o = asObjective(r[dim.name]?.value);
    const notes = o ? gapText(coverage.resultGaps[o], labels) : [];
    return notes.length > 0 ? [[rowKey(tile.fields, r), notes]] : [];
  });

/** rowKey → ["Unclassified"] for a table whose first dimension is the campaign name. */
const campaignRowNotes = (tile: LookerTile, labels: TileNoteLabels): Array<[string, string[]]> => {
  const first = tile.fields.find((f) => f.kind === 'dimension');
  if (tile.type !== 'table' || !first || !CAMPAIGN_DIM.test(first.name)) return [];
  return tile.rows.flatMap((r): Array<[string, string[]]> => {
    const v = r[first.name]?.value;
    return classifyCampaignName(v == null ? null : String(v)).conforms ? [] : [[rowKey(tile.fields, r), [labels.unclassified]]];
  });
};

/** Joins per-row note lists by key, keeping first-seen order. */
const mergeRows = (entries: ReadonlyArray<[string, string[]]>): Record<string, string[]> =>
  entries.reduce<Record<string, string[]>>((acc, [k, notes]) => ({ ...acc, [k]: unique([...(acc[k] ?? []), ...notes]) }), {});

/**
 * Result badges follow the page's channel filter, as the Result tiles do: only
 * the selected channels' Result gaps. Raw platform ids, as the filter sends them.
 * undefined / [] = every channel, unchanged. Box 8's lines (metricGaps,
 * missingDays, naming) cover every channel and are never restricted.
 */
export const restrictToChannels = (coverage: LookerCoverage, channels: readonly string[] | undefined): LookerCoverage => {
  if (!channels || channels.length === 0) return coverage;
  const keep = new Set(channels);
  const only = (gaps: readonly MetricGap[]) => gaps.filter((g) => keep.has(g.platform));
  return {
    ...coverage,
    resultGaps: {
      awareness: only(coverage.resultGaps.awareness),
      consideration: only(coverage.resultGaps.consideration),
      conversion: only(coverage.resultGaps.conversion),
    },
  };
};

/** The coverage the badges read: a ready read, restricted to the selected channels; else null. */
export const badgeCoverageOf = (state: CoverageState | undefined, channels: readonly string[] | undefined): LookerCoverage | null =>
  state?.status === 'ready' ? restrictToChannels(state.data, channels) : null;

export const tileNotesOf = (
  tile: LookerTile,
  coverage: LookerCoverage | null,
  selectedObjective: string | null,
  labels: TileNoteLabels,
): TileNotes => {
  if (tile.error || tile.type === 'app') return NO_TILE_NOTES;
  const hasResult = tile.fields.some(isResultField);
  const objDim = tile.fields.find((f) => f.kind === 'dimension' && OBJECTIVES_DIM.test(f.name));
  const resultRows = hasResult && coverage && objDim ? objectiveRowNotes(tile, objDim, coverage, labels) : [];
  const one = asObjective(selectedObjective);
  const resultCard = hasResult && coverage && !objDim && one ? gapText(coverage.resultGaps[one], labels) : [];
  // Only a table draws row chips; a chart by objective says them once on its card instead.
  const asTable = tile.type === 'table';
  return {
    card: unique([...resultCard, ...(asTable ? [] : resultRows.flatMap(([, n]) => n))]),
    rows: mergeRows([...(asTable ? resultRows : []), ...campaignRowNotes(tile, labels)]),
  };
};

/** A row's name for a reader: its dimension values, as the table shows them. */
export const rowNameOf = (fields: readonly LookerField[], row: LookerRow): string =>
  fields.filter((f) => f.kind === 'dimension').map((f) => {
    const c = row[f.name];
    return c?.rendered ?? (c?.value == null ? '—' : String(c.value));
  }).join(' | ');

/** `rows` (in order) that carry a note, by name, for the chat and the CSV. */
export const rowNotesByName = (tile: LookerTile, rows: readonly LookerRow[], notes: TileNotes): Array<[string, string[]]> =>
  rows.flatMap((r): Array<[string, string[]]> => {
    const n = notes.rows[rowKey(tile.fields, r)];
    return n && n.length > 0 ? [[rowNameOf(tile.fields, r), n]] : [];
  });

/** One box 8 coverage line; `platform` is set when the line is about one platform. */
export interface CoverageLine { platform: string | null; text: string }

export interface CoverageLineLabels {
  notReported: (platform: string, metric: CoverageMetric) => string;
  /** `dates` is the first three, then "…" when there are more. */
  missingDays: (platform: string, count: number, dates: string) => string;
  /**
   * `share` is the share of the campaigns with spend, printed whole by fmtNamingCoverage (D2's too, rules-04 E2):
   * floored to one decimal, with its %, so it never reads 100 early.
   */
  naming: (share: string, unclassified: number) => string;
  /** No campaign had spend: the share is not measurable, never 0%. */
  namingNone: string;
  /** A currency is forced: coverage still covers every currency of the workspace. */
  allCurrencies: string;
  complete: string;
  idleAll: string;
  idlePeriod: string;
  failed: string;
}

const MISSING_DATES_SHOWN = 3;

const namingLine = (c: LookerCoverage, labels: CoverageLineLabels): CoverageLine => ({
  platform: null,
  text: c.naming.share === null ? labels.namingNone : labels.naming(fmtNamingCoverage(c.naming.share), c.naming.unclassified),
});

/** Box 8's coverage section as lines. Loading has none; idle and failed say why there are none. */
export const coverageLinesOf = (state: CoverageState, labels: CoverageLineLabels): CoverageLine[] => {
  if (state.status === 'loading') return [];
  if (state.status === 'failed') return [{ platform: null, text: labels.failed }];
  if (state.status === 'idle') return [{ platform: null, text: state.reason === 'allWorkspaces' ? labels.idleAll : labels.idlePeriod }];
  const c = state.data;
  const gaps = c.metricGaps.map((g): CoverageLine => ({ platform: g.platform, text: labels.notReported(g.platform, g.metric) }));
  const missing = c.missingDays.map((m): CoverageLine => {
    const shown = m.days.slice(0, MISSING_DATES_SHOWN).join(', ');
    const dates = m.days.length > MISSING_DATES_SHOWN ? `${shown}, …` : shown;
    return { platform: m.platform, text: labels.missingDays(m.platform, m.days.length, dates) };
  });
  const issues = [...gaps, ...missing];
  const body = issues.length > 0 ? issues : [{ platform: null, text: labels.complete }];
  // Under a forced currency the coverage reads are not filtered: say so. The naming share counts campaigns, so it adds
  // no money and stands in any currency (rules-04 E2).
  const every: CoverageLine[] = state.currency ? [{ platform: null, text: labels.allCurrencies }] : [];
  return [...body, ...every, namingLine(c, labels)];
};
